import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, Play, ArrowUp, ArrowDown, Sparkles, Trash2, Wand2, Download, Film, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";
import InstantMontageCard from "@/components/app/InstantMontageCard";
import SoundtrackCard from "@/components/app/SoundtrackCard";
import StylePicker from "@/components/app/StylePicker";

export default function EventDetail() {
  const { id } = useParams();
  useSeo({ title: "Manage event", noindex: true, path: `/events/${id}` });
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [subs, setSubs] = useState([]);
  const [intro, setIntro] = useState("");
  const [outro, setOutro] = useState("");
  const [busyAi, setBusyAi] = useState(false);
  const [exportState, setExportState] = useState({ status: "idle" });
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [features, setFeatures] = useState({ export_enabled: true, ai_enabled: true, keepsake_enabled: true });
  const [bookLoading, setBookLoading] = useState(false);

  const reload = async () => {
    const [e, s] = await Promise.all([api.get(`/events/${id}`), api.get(`/events/${id}/submissions`)]);
    setEvent(e.data); setIntro(e.data.intro_title || ""); setOutro(e.data.outro_title || "");
    setSubs(s.data);
  };
  useEffect(() => { reload().catch(() => toast.error("Event not found")); }, [id]);

  useEffect(() => {
    api.get("/features").then((r) => setFeatures(r.data)).catch(() => {});
  }, []);

  // Poll export status when rendering
  useEffect(() => {
    const fetchStatus = () => api.get(`/events/${id}/export`).then((r) => {
      setExportState((prev) => {
        if (prev.status === "rendering" && r.data.status === "ready") reload().catch(() => {});
        return r.data;
      });
    }).catch(() => {});
    fetchStatus();
    const interval = setInterval(() => {
      setExportState((s) => {
        if (s.status === "rendering") fetchStatus();
        return s;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, [id]);

  const startExport = async () => {
    setExporting(true);
    try {
      await api.post(`/events/${id}/export`);
      toast.success("Rendering your film — this may take a minute");
      setExportState({ status: "rendering" });
    } catch { toast.error("Export failed to start"); }
    finally { setExporting(false); }
  };

  const downloadKeepsake = async () => {
    setBookLoading(true);
    try {
      const res = await api.get(`/events/${id}/keepsake`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(event?.title || "chapter").replace(/[^a-z0-9 _-]/gi, "")} - Keepsake Book.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Keepsake book downloaded");
    } catch (e) {
      const detail = e.response?.data?.detail || "Book generation failed";
      toast.error(typeof detail === "string" ? detail : "Book generation failed");
    } finally { setBookLoading(false); }
  };

  const orderedSubs = useMemo(() => {
    if (!event) return subs;
    const order = event.clip_order && event.clip_order.length ? event.clip_order : subs.map((s) => s.id);
    const map = Object.fromEntries(subs.map((s) => [s.id, s]));
    const seq = order.map((sid) => map[sid]).filter(Boolean);
    const extras = subs.filter((s) => !order.includes(s.id));
    return [...seq, ...extras];
  }, [subs, event]);

  const shareUrl = event ? `${window.location.origin}/c/${event.share_token}` : "";

  const copyShare = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied");
  };

  const move = async (i, dir) => {
    const ids = orderedSubs.map((s) => s.id);
    const ni = i + dir;
    if (ni < 0 || ni >= ids.length) return;
    [ids[i], ids[ni]] = [ids[ni], ids[i]];
    await api.patch(`/events/${id}`, { clip_order: ids });
    reload();
  };

  const saveTitles = async () => {
    await api.patch(`/events/${id}`, { intro_title: intro, outro_title: outro });
    toast.success("Saved");
    reload();
  };

  const aiTitles = async () => {
    setBusyAi(true);
    try {
      const { data } = await api.post(`/events/${id}/ai/title`, {});
      setIntro(data.intro || intro); setOutro(data.outro || outro);
      toast.success("AI titles generated");
    } catch (e) {
      toast.error("AI titles failed");
    } finally { setBusyAi(false); }
  };

  const aiGroup = async () => {
    setBusyAi(true);
    try {
      const { data } = await api.post(`/events/${id}/ai/group`);
      toast.success(`Grouped ${data.count} clips by theme`);
      reload();
    } catch (e) { toast.error("AI grouping failed"); }
    finally { setBusyAi(false); }
  };

  const deleteEvent = async () => {
    if (!confirm("Delete this event and all its submissions?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Event deleted");
    navigate("/dashboard");
  };

  if (!event) return (<div className="min-h-screen bg-[var(--em-bg)]"><Header /><div className="p-10 text-[var(--em-ink-soft)]">Loading…</div></div>);

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(shareUrl)}`;

  return (
    <div className="min-h-screen bg-[var(--em-bg)]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-28 md:pb-12">
        <div className="flex items-start justify-between flex-wrap gap-6">
          <div>
            <p className="font-mono-label text-[var(--em-clay)]">{event.theme}</p>
            <h1 className="font-serif-em text-4xl md:text-5xl mt-3" data-testid="event-title">{event.title}</h1>
            <p className="mt-2 text-[var(--em-ink-soft)] max-w-2xl">{event.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate(`/events/${id}/play`)} data-testid="event-play-btn" className="rounded-full bg-[var(--em-night)] text-white hover:bg-black btn-lift"><Play size={16} className="mr-2" />Play reel</Button>
            <Button variant="outline" onClick={deleteEvent} data-testid="event-delete-btn"><Trash2 size={16} /></Button>
          </div>
        </div>

        {features.export_enabled && (
          <InstantMontageCard
            eventId={id}
            subsCount={subs.length}
            exportState={exportState}
            onStarted={(data) => {
              setExportState((s) => ({ ...s, status: "rendering", stage: "AI is directing your montage", summary: data.summary || s.summary }));
              reload().catch(() => {});
            }}
          />
        )}

        <div className="grid lg:grid-cols-3 gap-6 mt-10">
          {/* Left: Share + AI */}
          <div className="space-y-6">
            <Card className="bg-white border-[var(--em-line)]">
              <CardContent className="p-6">
                <p className="font-mono-label text-[var(--em-sage)]">Invite contributors</p>
                <h3 className="font-serif-em text-xl mt-3">Share one link.</h3>
                <div className="mt-4 flex items-center gap-2">
                  <Input readOnly value={shareUrl} data-testid="event-share-link" className="bg-[var(--em-bg)] h-11 text-sm" />
                  <Button data-testid="event-copy-link-btn" onClick={copyShare} variant="outline" className="h-11"><Copy size={16} /></Button>
                </div>
                <div className="mt-5 flex items-center gap-4">
                  <img src={qrUrl} alt="QR" className="w-32 h-32 rounded-md border border-[var(--em-line)] bg-white" />
                  <div className="text-sm text-[var(--em-ink-soft)]">
                    Scan to contribute from a phone — no login needed.
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-[var(--em-line)]">
              <CardContent className="p-6">
                <p className="font-mono-label text-[var(--em-clay)]">AI assist</p>
                <h3 className="font-serif-em text-xl mt-3">Let Chapter help.</h3>
                <div className="mt-4 space-y-2">
                  <Button data-testid="ai-titles-btn" disabled={busyAi} variant="outline" onClick={aiTitles} className="w-full justify-start"><Wand2 className="mr-2 h-4 w-4" /> Generate intro & outro titles</Button>
                  <Button data-testid="ai-group-btn" disabled={busyAi || subs.length === 0} variant="outline" onClick={aiGroup} className="w-full justify-start"><Sparkles className="mr-2 h-4 w-4" /> Group clips by theme</Button>
                </div>
              </CardContent>
            </Card>

            <SoundtrackCard eventId={id} event={event} onChanged={reload} />

            <StylePicker eventId={id} styles={features.styles} value={event.style} onChanged={reload} />

            {features.export_enabled && (
            <Card className="bg-white border-[var(--em-line)]">
              <CardContent className="p-6">
                <p className="font-mono-label text-[var(--em-clay)]">Export</p>
                <h3 className="font-serif-em text-xl mt-3">Render final film</h3>
                <p className="text-sm text-[var(--em-ink-soft)] mt-1">Stitches all clips + intro/outro + music into one HD MP4.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button data-testid="export-start-btn" onClick={startExport} disabled={exporting || subs.length === 0 || exportState.status === "rendering"} className="rounded-full bg-[var(--em-night)] text-white hover:bg-black btn-lift">
                    <Film className="mr-2 h-4 w-4" />
                    {exportState.status === "rendering" ? "Rendering…" : exportState.status === "ready" ? "Re-render" : "Render film"}
                  </Button>
                  {exportState.status === "ready" && exportState.download_url && (
                    <a href={exportState.download_url} target="_blank" rel="noreferrer">
                      <Button data-testid="export-download-btn" variant="outline" className="rounded-full"><Download className="mr-2 h-4 w-4" />Download MP4</Button>
                    </a>
                  )}
                </div>
                {exportState.status === "rendering" && <p className="text-xs text-[var(--em-ink-soft)] mt-3" data-testid="export-status">{exportState.stage ? `${exportState.stage}…` : "Rendering"} — keep this tab open. Larger films can take a few minutes.</p>}
                {exportState.status === "failed" && <p className="text-xs text-red-600 mt-3" data-testid="export-error">Render failed. Try again — make sure clips have valid audio/video.</p>}
              </CardContent>
            </Card>
            )}

            {features.keepsake_enabled && (
            <Card className="bg-white border-[var(--em-line)] relative overflow-hidden">
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[var(--em-clay)]/10" />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-[var(--em-sage)]/10" />
              <CardContent className="p-6 relative">
                <p className="font-mono-label text-[var(--em-sage)]">Printable memento</p>
                <h3 className="font-serif-em text-xl mt-3">Keepsake book</h3>
                <p className="text-sm text-[var(--em-ink-soft)] mt-1">
                  A printable PDF booklet with a portrait of every contributor,
                  what they said, and a QR code that plays the full film.
                </p>
                <div className="mt-4 grid grid-cols-3 gap-1.5">
                  <div className="aspect-[3/4] rounded-md bg-[var(--em-bg)] border border-[var(--em-line)] grid place-items-center text-[10px] text-[var(--em-ink-soft)]">Cover</div>
                  <div className="aspect-[3/4] rounded-md bg-[var(--em-bg)] border border-[var(--em-line)] grid place-items-center text-[10px] text-[var(--em-ink-soft)]">Voices</div>
                  <div className="aspect-[3/4] rounded-md bg-[var(--em-bg)] border border-[var(--em-line)] grid place-items-center text-[10px] text-[var(--em-ink-soft)]">QR + Film</div>
                </div>
                <Button
                  data-testid="keepsake-download-btn"
                  onClick={downloadKeepsake}
                  disabled={bookLoading || subs.length === 0}
                  className="mt-5 w-full rounded-full bg-[var(--em-clay)] hover:bg-[var(--em-clay-deep)] text-white btn-lift"
                >
                  <BookOpen className="mr-2 h-4 w-4" />
                  {bookLoading ? "Preparing your book…" : "Download keepsake PDF"}
                </Button>
                <p className="text-xs text-[var(--em-ink-soft)] mt-3">
                  Print at home, or use a service like Blurb, Lulu, or your local printer.
                </p>
              </CardContent>
            </Card>
            )}
          </div>

          {/* Right: Tabs */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="reel">
              <TabsList className="bg-[var(--em-line)]/60">
                <TabsTrigger value="reel" data-testid="tab-reel">Reel</TabsTrigger>
                <TabsTrigger value="titles" data-testid="tab-titles">Titles</TabsTrigger>
                <TabsTrigger value="prompts" data-testid="tab-prompts">Prompts</TabsTrigger>
              </TabsList>

              <TabsContent value="reel" className="mt-5">
                {orderedSubs.length === 0 ? (
                  <div className="bg-white border border-dashed border-[var(--em-line)] rounded-2xl p-10 text-center" data-testid="reel-empty">
                    <h3 className="font-serif-em text-2xl">Waiting on the first voice.</h3>
                    <p className="text-[var(--em-ink-soft)] mt-2">Share your link — submissions will appear here.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orderedSubs.map((s, i) => (
                      <div key={s.id} data-testid={`sub-row-${s.id}`} className="bg-white border border-[var(--em-line)] rounded-2xl p-3 flex gap-4 items-center soft-card">
                        <div className="w-40 h-24 bg-black rounded-lg overflow-hidden flex-shrink-0">
                          <video src={fileUrl(s.id)} className="w-full h-full object-cover" preload="metadata" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono-label text-[var(--em-ink-soft)]">Clip {String(i+1).padStart(2, "0")}</span>
                            {s.theme_group && <span className="text-xs bg-[var(--em-sage)]/20 text-[var(--em-sage)] px-2 py-0.5 rounded-full">{s.theme_group.replaceAll("_"," ")}</span>}
                          </div>
                          <h4 className="font-serif-em text-lg mt-1 truncate">{s.contributor_name}</h4>
                          <p className="text-sm text-[var(--em-ink-soft)] truncate">{s.prompt}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button size="icon" variant="ghost" data-testid={`sub-up-${s.id}`} onClick={() => move(i, -1)}><ArrowUp size={16} /></Button>
                          <Button size="icon" variant="ghost" data-testid={`sub-down-${s.id}`} onClick={() => move(i, 1)}><ArrowDown size={16} /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="titles" className="mt-5">
                <Card className="bg-white border-[var(--em-line)]">
                  <CardContent className="p-6 space-y-5">
                    <div>
                      <Label className="font-mono-label">Intro title</Label>
                      <Input data-testid="intro-title-input" value={intro} onChange={(e) => setIntro(e.target.value)} className="mt-2 h-11 bg-[var(--em-bg)]" />
                    </div>
                    <div>
                      <Label className="font-mono-label">Outro title</Label>
                      <Input data-testid="outro-title-input" value={outro} onChange={(e) => setOutro(e.target.value)} className="mt-2 h-11 bg-[var(--em-bg)]" />
                    </div>
                    <Button data-testid="save-titles-btn" onClick={saveTitles} className="rounded-full bg-[var(--em-clay)] hover:bg-[var(--em-clay-deep)] text-white btn-lift">Save titles</Button>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="prompts" className="mt-5">
                <Card className="bg-white border-[var(--em-line)]">
                  <CardContent className="p-6">
                    <p className="text-sm text-[var(--em-ink-soft)] mb-3">These prompts guide guests when they contribute.</p>
                    <ul className="space-y-2">
                      {event.prompts.map((p, i) => (
                        <li key={i} className="text-[var(--em-ink)] border border-[var(--em-line)] rounded-xl px-4 py-3 bg-[var(--em-bg)]">{p}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
